import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LoseModalProps {
  isOpen: boolean;
  onClose: () => void;
  price: number;
  word: string;
  creatorName?: string;
}

export const LoseModal: React.FC<LoseModalProps> = ({
  isOpen,
  onClose,
  price,
  word,
  creatorName = 'the creator',
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 20,
            }}
            className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center relative overflow-hidden border-4 border-red-300"
            onClick={e => e.stopPropagation()}
          >
            {/* Animated background elements */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.1, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute inset-0 bg-red-100 rounded-2xl"
            />

            {/* Sad face Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                stiffness: 200,
                damping: 10,
                delay: 0.2,
              }}
              className="text-8xl mb-4 relative z-10"
            >
              💸
            </motion.div>

            {/* Message */}
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-4xl font-extrabold text-red-600 mb-2 relative z-10"
            >
              OH NO!
            </motion.h2>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-gray-800 mb-4 relative z-10"
            >
              You ran out of guesses!
            </motion.p>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-lg p-4 mb-4 relative z-10 border-2 border-red-200"
            >
              <p className="text-lg text-gray-700 mb-2">The word was:</p>
              <p className="text-4xl font-bold text-red-600 tracking-wider">{word}</p>
            </motion.div>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg p-6 mb-6 relative z-10 shadow-lg"
            >
              <motion.p
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="text-3xl font-bold mb-2"
              >
                ${price.toFixed(2)}
              </motion.p>
              <p className="text-lg">You owe {creatorName}!</p>
            </motion.div>

            <div className="flex justify-center relative z-10">
              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="bg-gray-700 hover:bg-gray-800 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
              >
                Close
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
